import React, { useState, useEffect, useCallback } from 'react';
import {
  Input, Button, message, Spin, Checkbox
} from 'antd';
import {
  SearchOutlined, UserOutlined, MailOutlined, PhoneOutlined,
  CrownOutlined, DollarOutlined, ShoppingOutlined,
  ShopOutlined, TruckOutlined, TeamOutlined,
  PlusOutlined, MinusOutlined, CheckOutlined
} from '@ant-design/icons';
import UserService from '../../utils/api/users';
import '../../scss/EditUserModal.scss';

const EditUserModal = ({ user, visible, onClose, onSave, currentAdminId }) => {
  const [searchText, setSearchText] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModified, setIsModified] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('all');

  const loadUsers = useCallback(async () => {
    if (!user || !visible) return;

    try {
      setInitialLoading(true);
      setIsModified(false);

      const userRole = user.role?.toString();

      if (userRole === "3" || userRole === "5") {

        try {
          const allClientsResponse = await UserService.GetClients();

          let allClients = [];
          if (Array.isArray(allClientsResponse)) {
            allClients = allClientsResponse;
          } else if (allClientsResponse?.data && Array.isArray(allClientsResponse.data)) {
            allClients = allClientsResponse.data;
          }

          let assignedClients = [];
          if (userRole === "3") {
            const assignedResponse = await UserService.GetClients({ buyerId: user.id });

            if (Array.isArray(assignedResponse)) {
              assignedClients = assignedResponse;
            } else if (assignedResponse?.data && Array.isArray(assignedResponse.data)) {
              assignedClients = assignedResponse.data;
            }
          } else if (userRole === "5") {
            const assignedResponse = await UserService.GetClients({ sellerId: user.id });

            if (Array.isArray(assignedResponse)) {
              assignedClients = assignedResponse;
            } else if (assignedResponse?.data && Array.isArray(assignedResponse.data)) {
              assignedClients = assignedResponse.data;
            }
          }

          setAllUsers(allClients);
          setAssignedUsers(assignedClients);
          setSelectedUsers(assignedClients.map(c => c.id));

        } catch (error) {
          message.error('Error al cargar los clientes');
        }

      } else if (userRole === "4") {
        try {
          const assignedResponse = await UserService.GetAssignedPersonnel(user.id);

          let assignedPersonnel = [];
          if (assignedResponse?.data) {
            const { sellers = [], buyers = [] } = assignedResponse.data;
            assignedPersonnel = [...sellers, ...buyers];
          }

          const [buyersResponse, sellersResponse] = await Promise.all([
            UserService.GetClientsAdmin({ role: '3', limit: 1000 }),
            UserService.GetClientsAdmin({ role: '5', limit: 1000 })
          ]);

          const allBuyers = buyersResponse?.data?.users || [];
          const allSellers = sellersResponse?.data?.users || [];
          const allPersonnel = [...allBuyers, ...allSellers];

          setAllUsers(allPersonnel);
          setAssignedUsers(assignedPersonnel);
          setSelectedUsers(assignedPersonnel.map(p => p.id));

        } catch (error) {
          message.error('Error al cargar el personal');
        }
      }

    } catch (error) {
      message.error('Error al cargar los datos');
    } finally {
      setInitialLoading(false);
    }
  }, [user, visible]);

  useEffect(() => {
    if (visible && user) {
      loadUsers();
    } else {
      setAllUsers([]);
      setAssignedUsers([]);
      setSelectedUsers([]);
      setSearchText('');
      setIsModified(false);
      setInitialLoading(true);
      setSelectedTab('all');
    }
  }, [visible, user, loadUsers]);

  const getRoleLabel = (role) => {
    const roleMapping = {
      "1": "Administrador",
      "2": "Finanzas",
      "3": "Comprador",
      "4": "Cliente",
      "5": "Vendedor",
      "6": "Proveedor",
      "9": "Repartidor"
    };
    return roleMapping[role?.toString()] || `Rol ${role}`;
  };

  const getRoleIcon = (role) => {
    const roleIcons = {
      "1": <CrownOutlined />,
      "2": <DollarOutlined />,
      "3": <ShoppingOutlined />,
      "4": <UserOutlined />,
      "5": <ShopOutlined />,
      "6": <TeamOutlined />,
      "9": <TruckOutlined />
    };
    return roleIcons[role?.toString()] || <UserOutlined />;
  };

  const getTargetUserLabel = (userRole) => {
    const userRoleStr = userRole?.toString();
    if (userRoleStr === "3" || userRoleStr === "5") {
      return "clientes";
    } else if (userRoleStr === "4") {
      return "personal (vendedores/compradores)";
    }
    return "usuarios";
  };

  const getTargetUserLabelSingular = (userRole) => {
    const userRoleStr = userRole?.toString();
    if (userRoleStr === "3" || userRoleStr === "5") {
      return "cliente";
    } else if (userRoleStr === "4") {
      return "personal";
    }
    return "usuario";
  };

  const getModalTitle = (userRole) => {
    const userRoleStr = userRole?.toString();
    if (userRoleStr === "3") {
      return `Gestionar clientes del comprador`;
    } else if (userRoleStr === "5") {
      return `Gestionar clientes del vendedor`;
    } else if (userRoleStr === "4") {
      return `Gestionar personal del cliente`;
    }
    return `Gestionar usuarios`;
  };

  const handleUserSelection = (userId, checked) => {
    const newSelection = checked
      ? [...selectedUsers, userId]
      : selectedUsers.filter(id => id !== userId);

    setSelectedUsers(newSelection);

    const originalIds = assignedUsers.map(u => u.id);
    const hasChanges =
      newSelection.length !== originalIds.length ||
      !newSelection.every(id => originalIds.includes(id)) ||
      !originalIds.every(id => newSelection.includes(id));

    setIsModified(hasChanges);
  };

  const handleSelectAll = (checked) => {
    const filteredIds = getFilteredUsers().map(u => u.id);
    const newSelection = checked
      ? [...new Set([...selectedUsers, ...filteredIds])]
      : selectedUsers.filter(id => !filteredIds.includes(id));

    setSelectedUsers(newSelection);
    setIsModified(true);
  };

  const getButtonText = () => {
    if (!isModified) return "Guardar cambios";

    const assignedCount = assignedUsers.length;
    const selectedCount = selectedUsers.length;

    if (selectedCount > assignedCount) {
      const diff = selectedCount - assignedCount;
      const label = getTargetUserLabelSingular(user.role);
      return (
        <>
          <PlusOutlined /> Asignar {diff} {diff === 1 ? label : label + 's'}
        </>
      );
    } else if (selectedCount < assignedCount) {
      const diff = assignedCount - selectedCount;
      const label = getTargetUserLabelSingular(user.role);
      return (
        <>
          <MinusOutlined /> Desasignar {diff} {diff === 1 ? label : label + 's'}
        </>
      );
    }
    return "Guardar cambios";
  };

  const handleSave = async () => {
    if (!currentAdminId) {
      message.error('Error: No se pudo identificar al administrador. Por favor, inicie sesión nuevamente.');
      return;
    }

    if (!user) {
      message.error('Error: Usuario no válido');
      return;
    }

    try {
      setLoading(true);
      const userRole = user.role?.toString();

      const originalAssignedIds = assignedUsers.map(u => u.id);
      const usersToAdd = selectedUsers.filter(id => !originalAssignedIds.includes(id));
      const usersToRemove = originalAssignedIds.filter(id => !selectedUsers.includes(id));

      if (usersToAdd.length === 0 && usersToRemove.length === 0) {
        message.info('No hay cambios para guardar');
        setLoading(false);
        return;
      }

      let successMessage = '';
      let updatedAssignedUsers = [];

      if (userRole === "3") {
        for (const clientId of usersToAdd) {
          await UserService.AssignClientsToPersonnel({
            clientId: clientId,
            adminId: currentAdminId,
            addBuyers: [user.id],
            addSellers: [],
            removeBuyers: [],
            removeSellers: []
          });
        }

        for (const clientId of usersToRemove) {
          await UserService.AssignClientsToPersonnel({
            clientId: clientId,
            adminId: currentAdminId,
            addBuyers: [],
            addSellers: [],
            removeBuyers: [user.id],
            removeSellers: []
          });
        }

        successMessage = usersToAdd.length > 0 && usersToRemove.length > 0
          ? `${usersToAdd.length} clientes asignados y ${usersToRemove.length} desasignados correctamente`
          : usersToAdd.length > 0
            ? `${usersToAdd.length} cliente(s) asignado(s) correctamente`
            : `${usersToRemove.length} cliente(s) desasignado(s) correctamente`;

      } else if (userRole === "5") {
        for (const clientId of usersToAdd) {
          await UserService.AssignClientsToPersonnel({
            clientId: clientId,
            adminId: currentAdminId,
            addBuyers: [],
            addSellers: [user.id],
            removeBuyers: [],
            removeSellers: []
          });
        }

        for (const clientId of usersToRemove) {
          await UserService.AssignClientsToPersonnel({
            clientId: clientId,
            adminId: currentAdminId,
            addBuyers: [],
            addSellers: [],
            removeBuyers: [],
            removeSellers: [user.id]
          });
        }

        successMessage = usersToAdd.length > 0 && usersToRemove.length > 0
          ? `${usersToAdd.length} clientes asignados y ${usersToRemove.length} desasignados correctamente`
          : usersToAdd.length > 0
            ? `${usersToAdd.length} cliente(s) asignado(s) correctamente`
            : `${usersToRemove.length} cliente(s) desasignado(s) correctamente`;

      } else if (userRole === "4") {
        const assignmentData = {
          clientId: user.id,
          adminId: currentAdminId,
          addBuyers: [],
          addSellers: [],
          removeBuyers: [],
          removeSellers: []
        };

        usersToAdd.forEach(userId => {
          const userToAdd = allUsers.find(u => u.id === userId);
          if (userToAdd) {
            const role = userToAdd.role?.toString();
            if (role === "3") {
              assignmentData.addBuyers.push(userId);
            } else if (role === "5") {
              assignmentData.addSellers.push(userId);
            }
          }
        });

        usersToRemove.forEach(userId => {
          const userToRemove = allUsers.find(u => u.id === userId);
          if (userToRemove) {
            const role = userToRemove.role?.toString();
            if (role === "3") {
              assignmentData.removeBuyers.push(userId);
            } else if (role === "5") {
              assignmentData.removeSellers.push(userId);
            }
          }
        });


        if (assignmentData.addBuyers.length > 0 || assignmentData.addSellers.length > 0 ||
          assignmentData.removeBuyers.length > 0 || assignmentData.removeSellers.length > 0) {
          await UserService.AssignClientsToPersonnel(assignmentData);
        }

        const totalAdded = assignmentData.addBuyers.length + assignmentData.addSellers.length;
        const totalRemoved = assignmentData.removeBuyers.length + assignmentData.removeSellers.length;

        if (totalAdded > 0 && totalRemoved > 0) {
          successMessage = `${totalAdded} miembros asignados y ${totalRemoved} desasignados correctamente`;
        } else if (totalAdded > 0) {
          successMessage = `${totalAdded} miembro(s) asignado(s) correctamente`;
        } else if (totalRemoved > 0) {
          successMessage = `${totalRemoved} miembro(s) desasignado(s) correctamente`;
        }

        updatedAssignedUsers = selectedUsers.map(userId => {
          const userFound = allUsers.find(u => u.id === userId);
          if (userFound) {
            console.log(`Encontrado usuario ${userId}:`, { name: userFound.name, role: userFound.role });
          } else {
            console.log(`No se encontró usuario con ID: ${userId}`);
          }
          return userFound;
        }).filter(user => user !== undefined);


      }

      if (successMessage) {
        message.success(successMessage);
      }

      onSave({
        success: true,
        userId: user.id,
        userRole: userRole,
        usersToAdd: usersToAdd,
        usersToRemove: usersToRemove,
        assignedUsers: userRole === "4" ? updatedAssignedUsers : undefined,
        allAvailableUsers: allUsers,
        currentAssignedUsers: assignedUsers,
        currentSelectedUsers: selectedUsers
      });

    } catch (error) {
      message.error(error.response?.data?.error || 'Ocurrió un error al guardar los cambios');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredUsers = () => {
    let filtered = allUsers.filter(u =>
      u.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchText.toLowerCase()) ||
      (u.phone && u.phone.includes(searchText))
    );

    if (selectedTab === 'assigned') {
      filtered = filtered.filter(u => assignedUsers.some(a => a.id === u.id));
    } else if (selectedTab === 'available') {
      filtered = filtered.filter(u => !assignedUsers.some(a => a.id === u.id));
    }

    return filtered;
  };

  const filteredAllUsers = getFilteredUsers();

  const allFilteredSelected = filteredAllUsers.length > 0 &&
    filteredAllUsers.every(u => selectedUsers.includes(u.id));

  const someFilteredSelected = filteredAllUsers.some(u => selectedUsers.includes(u.id)) &&
    !allFilteredSelected;

  if (!visible || !user) return null;

  return (
    <div className="edit-user-modal-overlay">
      <div className="edit-user-modal edit-user-modal-horizontal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-content">
            <h2 className="modal-title">
              {getModalTitle(user.role)}
            </h2>
            <div className="header-right">
              <div className="user-badge">
                <span className="user-badge-icon">{getRoleIcon(user.role)}</span>
                <span className="user-badge-name">{user.name}</span>
              </div>
              <span className="role-tag">{getRoleLabel(user.role)}</span>
            </div>
          </div>
        </div>

        <div className="modal-content">
          {initialLoading ? (
            <div className="loading-container">
              <Spin size="large" />
              <p>Cargando {getTargetUserLabel(user.role)}...</p>
            </div>
          ) : (
            <div className="horizontal-layout">
              <div className="left-panel">
                <div className="summary-card">
                  <div className="summary-item">
                    <span className="summary-label">Total</span>
                    <span className="summary-value">{allUsers.length}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Asignados</span>
                    <span className="summary-value">{assignedUsers.length}</span>
                  </div>
                  <div className="summary-item highlight">
                    <span className="summary-label">Seleccionados</span>
                    <span className="summary-value">{selectedUsers.length}</span>
                  </div>
                </div>

                <div className="filter-tabs">
                  <button
                    className={`tab-btn ${selectedTab === 'all' ? 'active' : ''}`}
                    onClick={() => setSelectedTab('all')}
                  >
                    Todos <span className="tab-count">{allUsers.length}</span>
                  </button>
                  <button
                    className={`tab-btn ${selectedTab === 'assigned' ? 'active' : ''}`}
                    onClick={() => setSelectedTab('assigned')}
                  >
                    Asignados <span className="tab-count">{assignedUsers.length}</span>
                  </button>
                  <button
                    className={`tab-btn ${selectedTab === 'available' ? 'active' : ''}`}
                    onClick={() => setSelectedTab('available')}
                  >
                    Disponibles <span className="tab-count">{allUsers.length - assignedUsers.length}</span>
                  </button>
                </div>

                {isModified && (
                  <div className="changes-bar">
                    <div className="changes-bar-progress">
                      <div
                        className="changes-bar-fill"
                        style={{
                          width: `${Math.min(100, (Math.abs(selectedUsers.length - assignedUsers.length) / Math.max(assignedUsers.length, 1)) * 100)}%`
                        }}
                      />
                    </div>
                    <span className="changes-bar-text">
                      {selectedUsers.length > assignedUsers.length
                        ? <><PlusOutlined /> Agregando {selectedUsers.length - assignedUsers.length}</>
                        : selectedUsers.length < assignedUsers.length
                          ? <><MinusOutlined /> Quitando {assignedUsers.length - selectedUsers.length}</>
                          : <><CheckOutlined /> Sin cambios</>
                      }
                    </span>
                  </div>
                )}

                <div className="legend">
                  <div className="legend-item">
                    <span className="legend-dot assigned"></span>
                    <span className="legend-text">Ya asignado</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot selected"></span>
                    <span className="legend-text">Seleccionado</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot available"></span>
                    <span className="legend-text">Disponible</span>
                  </div>
                </div>
              </div>

              <div className="right-panel">
                <div className="search-wrapper">
                  <Input
                    placeholder={`Buscar por nombre, email o teléfono...`}
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="search-input"
                    size="large"
                    allowClear
                  />
                  {searchText && (
                    <span className="search-results-count">
                      {filteredAllUsers.length} resultados
                    </span>
                  )}
                </div>

                {filteredAllUsers.length > 0 && (
                  <div className="select-all-wrapper">
                    <Checkbox
                      className="select-all-checkbox"
                      checked={allFilteredSelected}
                      indeterminate={someFilteredSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    >
                      <strong>Seleccionar todos</strong> los filtrados
                    </Checkbox>
                    <span className="selected-counter">
                      {selectedUsers.length} de {allUsers.length} seleccionados
                    </span>
                  </div>
                )}

                <div className="users-list">
                  {filteredAllUsers.length === 0 ? (
                    <div className="empty-state">
                      <div className="empty-icon"><SearchOutlined /></div>
                      <h4 className="empty-title">
                        {searchText ? 'No hay resultados' : `No hay ${getTargetUserLabel(user.role)} disponibles`}
                      </h4>
                      <p className="empty-description">
                        {searchText ?
                          'Intenta con otros términos de búsqueda' :
                          `No se encontraron ${getTargetUserLabel(user.role)} para asignar`
                        }
                      </p>
                    </div>
                  ) : (
                    filteredAllUsers.map(availableUser => {
                      const isAssigned = assignedUsers.some(u => u.id === availableUser.id);
                      const isSelected = selectedUsers.includes(availableUser.id);

                      return (
                        <div
                          key={availableUser.id}
                          className={`user-item ${isAssigned ? 'user-assigned' : ''} ${isSelected && !isAssigned ? 'user-selected' : ''}`}
                          onClick={() => handleUserSelection(availableUser.id, !isSelected)}
                        >
                          <div className="user-avatar">
                            {getRoleIcon(availableUser.role)}
                          </div>
                          <div className="user-info">
                            <div className="user-name-row">
                              <span className="user-name">
                                {availableUser.name || 'Nombre no disponible'}
                              </span>
                              <span className="user-role-tag">
                                {getRoleLabel(availableUser.role)}
                              </span>
                            </div>
                            <div className="user-details">
                              {availableUser.email && (
                                <span className="user-email">
                                  <MailOutlined /> {availableUser.email}
                                </span>
                              )}
                              {availableUser.phone && (
                                <span className="user-phone">
                                  <PhoneOutlined /> {availableUser.phone}
                                </span>
                              )}
                            </div>
                            {isAssigned && (
                              <span className="assigned-badge">
                                <CheckOutlined /> Asignado
                              </span>
                            )}
                          </div>
                          <Checkbox
                            className="user-checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleUserSelection(availableUser.id, e.target.checked);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <Button
            onClick={onClose}
            className="cancel-button"
            size="large"
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="primary"
            onClick={handleSave}
            className={`save-button ${isModified ? 'has-changes' : ''}`}
            size="large"
            loading={loading}
            disabled={!isModified || loading}
          >
            {getButtonText()}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;